import { atom } from 'recoil';
import { first, includes, last } from 'lodash-es';
import {
  monthDates,
  NC_RECORD_END_KEY,
  NC_RECORD_START_KEY, ORDERS_VIEW_END_KEY, ORDERS_VIEW_START_KEY,
  SALES_ORDER_END_KEY,
  SALES_ORDER_START_KEY, SHIPMENTS_VIEW_END_KEY, SHIPMENTS_VIEW_START_KEY,
  workWeeksForMonth,
} from '@data/calendar';

// ---------------------------------------------------------------------------
// Start-date initialization
// Reads localStorage based on the current URL path so each view remembers its
// own date range across page reloads.
// ---------------------------------------------------------------------------

let storedSalesOrderStartDate = localStorage.getItem(SALES_ORDER_START_KEY);
if (window.location.pathname.match(/nc/i)) storedSalesOrderStartDate = localStorage.getItem(NC_RECORD_START_KEY);
if (window.location.pathname.match(/qa/i)) storedSalesOrderStartDate = localStorage.getItem(NC_RECORD_START_KEY);
if (window.location.pathname.match(/orders/i)) storedSalesOrderStartDate = localStorage.getItem(ORDERS_VIEW_START_KEY);
if (window.location.pathname.match(/shipments/i)) storedSalesOrderStartDate = localStorage.getItem(SHIPMENTS_VIEW_START_KEY);

const firstOfStartMonth = storedSalesOrderStartDate ? new Date(parseInt(storedSalesOrderStartDate, 10)) : new Date();
// if we are in the first week of the month
if (!storedSalesOrderStartDate && firstOfStartMonth.getDate() <= 7 && firstOfStartMonth.getMonth() > 0) {
  const lastMonthWeek = last(
    workWeeksForMonth(
      monthDates()[firstOfStartMonth.getMonth() - 1],
    ),
  );

  firstOfStartMonth.setDate(first(lastMonthWeek).getDate());
  firstOfStartMonth.setMonth(first(lastMonthWeek).getMonth());
}

// ---------------------------------------------------------------------------
// End-date initialization
// ---------------------------------------------------------------------------

let storedSalesOrderEndDate = localStorage.getItem(SALES_ORDER_END_KEY);
if (window.location.pathname.match(/nc/i)) storedSalesOrderEndDate = localStorage.getItem(NC_RECORD_END_KEY);
if (window.location.pathname.match(/qa/i)) storedSalesOrderEndDate = localStorage.getItem(NC_RECORD_END_KEY);
if (window.location.pathname.match(/orders/i)) storedSalesOrderEndDate = localStorage.getItem(ORDERS_VIEW_END_KEY);
if (window.location.pathname.match(/shipments/i)) storedSalesOrderEndDate = localStorage.getItem(SHIPMENTS_VIEW_END_KEY);

const lastOfEndMonth = storedSalesOrderEndDate ? new Date(parseInt(storedSalesOrderEndDate, 10)) : new Date();
if (!storedSalesOrderEndDate) {
  lastOfEndMonth.setMonth(lastOfEndMonth.getMonth() + 3);
  lastOfEndMonth.setDate(1);
  lastOfEndMonth.setDate(lastOfEndMonth.getDate() - 1);
}

// ---------------------------------------------------------------------------
// Schedule / production view date-range atoms
// ---------------------------------------------------------------------------

export const scheduleViewStartDateAtom = atom<Date>({
  key: 'startDateAtom',
  default: firstOfStartMonth,
});

export const orderViewStartDateAtom = atom<Date>({
  key: 'orderViewStartDateAtom',
  default: firstOfStartMonth,
});

export const shipmentsViewStartDateAtom = atom<Date>({
  key: 'shipmentsViewStartDateAtom',
  default: firstOfStartMonth,
});

export const nonConformanceStartDateAtom = atom<Date>({
  key: 'nonConformanceViewStartDateAtom',
  default: firstOfStartMonth,
});

export const scheduleViewEndDateAtom = atom<Date>({
  key: 'endDateAtom',
  default: lastOfEndMonth,
});

export const orderViewEndDateAtom = atom<Date>({
  key: 'orderViewEndDateAtom',
  default: lastOfEndMonth,
});

export const shipmentsViewEndDateAtom = atom<Date>({
  key: 'shipmentsViewEndDateAtom',
  default: lastOfEndMonth,
});

export const nonConformanceViewEndDateAtom = atom<Date>({
  key: 'nonConformanceViewEndDateAtom',
  default: lastOfEndMonth,
});

// ---------------------------------------------------------------------------
// Schedule view filter atoms
// ---------------------------------------------------------------------------

export const orderWeeksAtom = atom<Date[][][]>({
  key: 'orderWeeksAtom',
  default: [],
});

export const showBodiesAtom = atom<boolean>({
  key: 'showBodiesAtom',
  default: includes(['all', 'body'], localStorage.getItem('partViewType')),
});

export const showNecksAtom = atom<boolean>({
  key: 'showNecksAtom',
  default: includes(['all', 'neck'], localStorage.getItem('partViewType')),
});

export const showLossesAtom = atom<boolean>({
  key: 'showLossesAtom',
  default: localStorage.getItem('ncShowLosses') === 'true',
});

export const showNCAtom = atom<boolean>({
  key: 'showNCAtom',
  default: localStorage.getItem('ncShowNC') === 'true',
});

export const timeMachineAtom = atom<Date>({
  key: 'timeMachineAtom',
  default: new Date(),
});

export const customerFilterAtom = atom<string>({
  key: 'customerFilterAtom',
  default: localStorage.getItem('customerFilter') || '',
});
